import type { Tables } from '@biopeak/db';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSession } from './session.tsx';
import { supabase } from './supabase.ts';

/**
 * Os dois canais de comunicação do lado do médico (build-prompt §3.2 e §4.2).
 *
 * São canais DIFERENTES de propósito, e a UI não deve fundi-los: o feed é mão
 * única (médico → paciente) e carrega orientação formal com estado de leitura e
 * confirmação; o chat é bidirecional e é conversa livre. Um comunicado que
 * vira mensagem de chat perde o recibo de leitura que existe justamente para o
 * médico saber se a orientação chegou.
 *
 * Nenhuma RPC nova foi necessária para escrever: as policies de INSERT do
 * médico em `feed_messages` e `chat_messages` já existem desde a fase 3. O que
 * elas exigem, e é fácil errar:
 *   - feed: `doctor_id` = `doctors.id` (o id da linha do médico)
 *   - chat: `sender_user_id` = `auth.uid()` (o id do USUÁRIO, não o do médico)
 * São duas chaves diferentes para a mesma pessoa, e trocá-las faz a policy
 * recusar sem explicar o porquê.
 */

export type FeedMessage = Tables<'feed_messages'>;
export type ChatMessage = Tables<'chat_messages'>;
export type NotificationSettings = Tables<'notification_settings'>;

// ------------------------------------------------------------ comunicados

export function useFeedMessages(patientId: string) {
  return useQuery({
    queryKey: ['feed-messages', patientId],
    queryFn: async (): Promise<FeedMessage[]> => {
      const { data, error } = await supabase
        .from('feed_messages')
        .select('*')
        .eq('patient_id', patientId)
        .order('sent_at', { ascending: false });

      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useSendFeedMessage(patientId: string) {
  const queryClient = useQueryClient();
  const { doctor } = useSession();

  return useMutation({
    mutationFn: async (body: string) => {
      if (!doctor) throw new Error('Sessão de médico ausente');

      const trimmed = body.trim();
      if (!trimmed) throw new Error('Comunicado vazio');

      const { error } = await supabase.from('feed_messages').insert({
        patient_id: patientId,
        doctor_id: doctor.id,
        body: trimmed,
      });
      if (error) throw error;
    },
    // A linha do tempo já renderiza `feed_sent` — sem invalidá-la o médico
    // enviaria o comunicado e não o veria aparecer no histórico ao lado.
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['feed-messages', patientId] }),
        queryClient.invalidateQueries({ queryKey: ['patient-timeline', patientId] }),
      ]);
    },
  });
}

// ------------------------------------------------------------------- chat

export function useChatMessages(patientId: string) {
  return useQuery({
    queryKey: ['chat-messages', patientId],
    queryFn: async (): Promise<ChatMessage[]> => {
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('patient_id', patientId)
        .order('sent_at', { ascending: true });

      if (error) throw error;
      return data ?? [];
    },
  });
}

/**
 * Assina o chat daquele paciente em tempo real.
 *
 * Invalida e refaz a consulta em vez de anexar o payload recebido: o payload do
 * Realtime não passa pelas mesmas garantias de ordenação da consulta, e uma
 * lista montada por duas fontes diferentes diverge em condição de corrida.
 *
 * A RLS também vale para o canal — o Supabase aplica as policies às mudanças
 * transmitidas, então isto não alcança conversa de paciente de outro médico
 * mesmo se o filtro abaixo fosse omitido.
 */
export function useChatRealtime(patientId: string) {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel(`doctor-chat:${patientId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `patient_id=eq.${patientId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['chat-messages', patientId] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [patientId, queryClient]);
}

export function useSendChatMessage(patientId: string) {
  const queryClient = useQueryClient();
  const { session } = useSession();

  return useMutation({
    mutationFn: async (body: string) => {
      if (!session) throw new Error('Sessão ausente');

      const trimmed = body.trim();
      if (!trimmed) throw new Error('Mensagem vazia');

      const { error } = await supabase.from('chat_messages').insert({
        patient_id: patientId,
        sender_role: 'doctor',
        // `auth.uid()`, não `doctor.id`: é o que a policy de INSERT compara.
        sender_user_id: session.user.id,
        body: trimmed,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['chat-messages', patientId] }),
        queryClient.invalidateQueries({ queryKey: ['patient-timeline', patientId] }),
      ]);
    },
  });
}

/**
 * Marca como lidas as mensagens que o PACIENTE mandou.
 *
 * As do próprio médico continuam não lidas até o paciente abrir o chat — a RPC
 * decide o lado por quem chama, então não há como marcar a própria mensagem
 * como lida e inventar um recibo.
 */
export function useMarkChatRead(patientId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('mark_chat_read', {
        p_patient_id: patientId,
      });
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['chat-messages', patientId] }),
  });
}

// --------------------------------------------------------------- config

/**
 * Prazos de escalonamento, parametrizados em banco (§5.3).
 *
 * Buscados e não hardcoded porque o build-prompt exige que sejam ajustáveis:
 * o "48h / 7 dias" foi uma decisão de produto, não uma constante de código, e
 * quando mudar precisa mudar num lugar só.
 */
export function useNotificationSettings() {
  return useQuery({
    queryKey: ['notification-settings'],
    staleTime: 60 * 60_000,
    queryFn: async (): Promise<NotificationSettings | null> => {
      const { data, error } = await supabase
        .from('notification_settings')
        .select('*')
        .maybeSingle();

      if (error) throw error;
      return data;
    },
  });
}

/**
 * Comunicado não lido há tempo demais — o sinal discreto do §5.3.
 *
 * Devolve os dias do comunicado não lido MAIS ANTIGO que já passou do prazo, ou
 * `null` quando não há nenhum. Só o mais antigo importa: o médico precisa saber
 * que existe algo pendente e há quanto tempo, não a lista inteira.
 *
 * Deliberadamente sem cor de status. O §10 é explícito que isto não vira alerta
 * vermelho automático — é sinal informativo, não gravidade clínica.
 */
export function useUnreadFeedDays(patientId: string): number | null {
  const { data: messages } = useFeedMessages(patientId);
  const { data: settings } = useNotificationSettings();

  if (!messages || !settings) return null;

  const limite = settings.unread_panel_indicator_days;
  const agora = Date.now();

  const dias = messages
    .filter((m) => m.read_at === null)
    .map((m) => Math.floor((agora - new Date(m.sent_at).getTime()) / 86_400_000));

  if (dias.length === 0) return null;

  const maisAntigo = Math.max(...dias);
  return maisAntigo >= limite ? maisAntigo : null;
}
